<script setup>
import { ref, onMounted, computed } from 'vue';
import { loadAluminumData } from './lib/data.js';
import {
  propertyColumns,
  getDistinctAlloys,
  getOptionsForPair,
  hasNotRecommended,
  hasSpecialConsiderations,
} from './lib/logic.js';
import { definitions, specialConsiderations } from './lib/definitions.js';

const data = ref(null);
const allA = ref([]);
const allB = ref([]);
const a = ref('');
const b = ref('');
const options = ref([]);
const notRecommended = ref(false);
const specialConsiderationsApply = ref(false);
// 'select' | 'results' | 'definitions' — same pattern as filler-metal's `step`.
const step = ref('select');

const productsLink = computed(() => {
  // Prefer first option link; otherwise fallback to generic page
  const link = options.value.find((o) => o.link)?.link;
  return link || 'https://www.hobartbrothers.com/resources/calculators/';
});

function displayAlloy(v) {
  return String(v).length === 3 ? `${v}.0` : String(v);
}

function rating(opt, key) {
  return (opt.properties && opt.properties[key]) || '';
}

function ratingLabel(opt, column) {
  const value = rating(opt, column.key);
  return `${opt.filler}, ${column.label}: ${value || 'no rating'}`;
}

async function load() {
  data.value = await loadAluminumData();
  const { alloyA, alloyB } = getDistinctAlloys(data.value);
  allA.value = alloyA;
  allB.value = alloyB;
  a.value = alloyA.includes('319') ? '319' : alloyA[0] || '';
  b.value = alloyB.includes('319') ? '319' : alloyB[0] || '';
  refresh();
}

function refresh() {
  if (!data.value) return;
  options.value = getOptionsForPair(data.value, a.value, b.value);
  notRecommended.value = hasNotRecommended(options.value);
  specialConsiderationsApply.value = hasSpecialConsiderations(options.value);
}

onMounted(load);
</script>

<template>
  <div>
    <!-- Calculator screen -->
    <div v-if="step === 'select'" class="hb-container hb-container-compact">
      <h1 class="hb-page-title">Aluminum Filler Selector</h1>
      <p class="picker-help">
        Choose base materials to be joined for recommended Aluminum Filler Metal Choice.
      </p>
      <div class="aluminum-selectors">
        <div class="aluminum-selector">
          <div class="aluminum-selector-label">Alloy A</div>
          <select v-model="a" class="picker hb-select" @change="refresh">
            <option v-for="v in allA" :key="v" :value="v">{{ displayAlloy(v) }}</option>
          </select>
        </div>
        <div class="aluminum-selector">
          <div class="aluminum-selector-label">Alloy B</div>
          <select v-model="b" class="picker hb-select" @change="refresh">
            <option v-for="v in allB" :key="v" :value="v">{{ displayAlloy(v) }}</option>
          </select>
        </div>
      </div>
      <div v-if="notRecommended" class="hb-note" style="margin-top: 8px">NOT RECOMMENDED</div>
      <div style="margin-top: 28px">
        <button
          class="hb-button-submit"
          :disabled="!options.length || notRecommended"
          @click="step = 'results'"
        >
          View Filler Metal Properties
        </button>
      </div>
    </div>

    <!-- Results screen -->
    <div v-else-if="step === 'results'" class="hb-container hb-container-compact">
      <h1 class="hb-page-title">Aluminum Filler Selector</h1>
      <p class="picker-help">
        Choose base materials to be joined for recommended Aluminum Filler Metal Choice.
      </p>
      <button type="button" class="hb-back-link" @click="step = 'select'">
        <span aria-hidden="true">←</span>
        Back
      </button>

      <div class="aluminum-alloys-display">
        <div class="aluminum-alloy-display">
          <div class="aluminum-alloy-label">Alloy A</div>
          <div class="aluminum-alloy-value">{{ displayAlloy(a) }}</div>
        </div>
        <div class="aluminum-alloy-display">
          <div class="aluminum-alloy-label">Alloy B</div>
          <div class="aluminum-alloy-value">{{ displayAlloy(b) }}</div>
        </div>
      </div>

      <div v-if="notRecommended" class="hb-note aluminum-warning">NOT RECOMMENDED</div>

      <div class="aluminum-legend">
        <strong>Legend:</strong> A = Best, B = Good, C = Fair, D = Poor, PWHT = Post Weld Heat
        Treat.
      </div>

      <!-- Filler metals are rows and the 8 property categories are vertical column headers, so the
           widest case (7 fillers) fits a phone without scrolling sideways. -->
      <div v-if="options.length" class="matrix-wrap">
        <div class="matrix">
          <div class="corner" aria-hidden="true"></div>
          <!-- role="img" + aria-label so assistive tech reads the whole category name; without it
               the rotated writing-mode text is announced letter by letter. Same idea on each cell,
               which otherwise reads as a bare "A" with no row or column context. -->
          <div
            v-for="col in propertyColumns"
            :key="col.key"
            class="head"
            role="img"
            :title="col.label"
            :aria-label="col.label"
          >
            {{ col.label }}
          </div>

          <template v-for="opt in options" :key="opt.filler + (opt.sort_number || '')">
            <div class="fill-name">
              <a v-if="opt.link" :href="opt.link" class="aluminum-product-link">{{ opt.filler }}</a>
              <span v-else class="aluminum-product-link">{{ opt.filler }}</span>
            </div>
            <div
              v-for="col in propertyColumns"
              :key="opt.filler + col.key"
              class="cell"
              role="img"
              :aria-label="ratingLabel(opt, col)"
            >
              {{ rating(opt, col.key) }}
            </div>
          </template>
        </div>
      </div>
      <div v-else class="hb-note" style="margin-top: 16px">No recommendations available.</div>

      <div v-if="specialConsiderationsApply" class="aluminum-special">
        <div class="hb-section-title">{{ specialConsiderations.heading }}</div>
        <template v-for="section in specialConsiderations.sections" :key="section.heading">
          <h3 class="aluminum-subheading">{{ section.heading }}</h3>
          <p v-for="(para, i) in section.paragraphs" :key="i" class="aluminum-prose">{{ para }}</p>
        </template>
      </div>

      <div class="aluminum-actions">
        <button type="button" class="hb-button-orange" @click="step = 'definitions'">
          Definitions
        </button>
        <a class="hb-button-orange aluminum-products-link" :href="productsLink">
          Hobart Aluminum Products
        </a>
      </div>

      <div class="aluminum-back-footer">
        <button class="hb-button hb-button-small" @click="step = 'select'">Back</button>
      </div>
    </div>

    <!-- Definitions screen -->
    <div v-else class="hb-container hb-container-compact">
      <h1 class="hb-page-title">Definitions</h1>
      <button type="button" class="hb-back-link" @click="step = 'results'">
        <span aria-hidden="true">←</span>
        Back
      </button>

      <div class="aluminum-definitions">
        <div class="hb-section-title">{{ specialConsiderations.heading }}</div>
        <template v-for="section in specialConsiderations.sections" :key="section.heading">
          <h3 class="aluminum-subheading">{{ section.heading }}</h3>
          <p v-for="(para, i) in section.paragraphs" :key="i" class="aluminum-prose">{{ para }}</p>
        </template>

        <template v-for="entry in definitions" :key="entry.heading">
          <div class="hb-section-title">{{ entry.heading }}</div>
          <p v-for="(para, i) in entry.paragraphs" :key="i" class="aluminum-prose">{{ para }}</p>
        </template>
      </div>

      <div class="aluminum-back-footer">
        <button class="hb-button hb-button-small" @click="step = 'results'">Back</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.aluminum-selectors {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 20px;
}

.aluminum-selector {
  flex: 1 1 160px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.aluminum-selector-label {
  font-weight: 600;
}

.aluminum-alloys-display {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin: 12px 0 20px;
}

.aluminum-alloy-display {
  min-width: 140px;
}

.aluminum-alloy-label {
  font-weight: 600;
  margin-bottom: 4px;
}

.aluminum-alloy-value {
  font-size: 22px;
  font-weight: 700;
}

.aluminum-warning {
  margin-bottom: 12px;
}

.aluminum-legend {
  margin-bottom: 12px;
}

.aluminum-special {
  margin-top: 20px;
}

.aluminum-definitions {
  margin-top: 16px;
}

.aluminum-definitions .hb-section-title {
  margin-top: 20px;
}

.aluminum-subheading {
  margin: 12px 0 0;
  font-size: 14px;
  font-weight: 700;
}

.aluminum-prose {
  margin: 4px 0 10px;
  font-size: 14px;
  line-height: 1.5;
}

/* Definitions sits beside Hobart Aluminum Products below the grid, per engineering feedback. */
.aluminum-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
}

.aluminum-products-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: #000;
}

.aluminum-product-link {
  display: block;
  text-decoration: none;
  color: #000;
  font-weight: 700;
}

.aluminum-product-link:hover {
  text-decoration: underline;
}

a.aluminum-product-link {
  cursor: pointer;
}

span.aluminum-product-link {
  cursor: default;
}

.aluminum-back-footer {
  margin-top: 24px;
}

@media (max-width: 480px) {
  .aluminum-alloy-value {
    font-size: 18px;
  }
}
</style>
